export type NormalizedProductProcess = {
  key: string;
  productProcessId?: string;
  processMasterId?: string;
  title: string;
  subtitle: string;
  text: string;
  karigar: any;
  media: any[];
  source: "flexible" | "legacy";
};

const LEGACY_PROCESS_DEFS = [
  { key: "designing", title: "Designing", detailKey: "designingDetails", names: ["designing", "design"] },
  { key: "weaving", title: "Weaving", detailKey: "weavingDetails", names: ["weaving", "weave"] },
  { key: "dying", title: "Dying", detailKey: "dyingDetails", names: ["dying", "dyeing", "dye"] },
  { key: "finishing", title: "Finishing", detailKey: "finishingDetails", names: ["finishing", "finish"] },
];

function hasProcessValue(process: NormalizedProductProcess) {
  return Boolean(
    process.title ||
      process.subtitle ||
      process.text ||
      process.karigar?.name ||
      process.media?.some((m: any) => m?.mediaURL || m?.mediaUrl)
  );
}

function normalizeFlexibleProcess(process: any, index: number): NormalizedProductProcess {
  const productProcessId = String(process?.productProcessId || "").trim();
  const processMasterId = String(process?.processMasterId || "").trim();
  return {
    key: productProcessId || `flexible-${index}`,
    productProcessId: productProcessId || undefined,
    processMasterId: processMasterId || undefined,
    title: String(process?.title || "").trim(),
    subtitle: String(process?.subtitle || "").trim(),
    text: String(process?.text || "").trim(),
    karigar: process?.karigar || null,
    media: Array.isArray(process?.media) ? process.media : [],
    source: "flexible",
  };
}

function normalizeLegacyProcess(product: any, def: (typeof LEGACY_PROCESS_DEFS)[number]): NormalizedProductProcess {
  const detail = product?.[def.detailKey];
  return {
    key: def.key,
    title: def.title,
    subtitle: "",
    text: "",
    karigar: detail?.karigar || null,
    media: Array.isArray(detail?.media) ? detail.media : [],
    source: "legacy",
  };
}

export function isFlexibleProcessWorkflow(product: any): boolean {
  if (String(product?.processWorkflowVersion || "").toLowerCase() === "flexible") {
    return true;
  }
  return Array.isArray(product?.flexibleUploadProcesses) && product.flexibleUploadProcesses.length > 0;
}

export function getFlexibleFirstProcesses(product: any): NormalizedProductProcess[] {
  if (!isFlexibleProcessWorkflow(product)) {
    return LEGACY_PROCESS_DEFS.map((def) => normalizeLegacyProcess(product, def)).filter(hasProcessValue);
  }
  const flexible = Array.isArray(product?.flexibleUploadProcesses)
    ? product.flexibleUploadProcesses
        .map(normalizeFlexibleProcess)
        .filter((process: NormalizedProductProcess) => hasProcessValue(process) || Boolean(process.productProcessId))
    : [];
  return flexible;
}

function findFlexibleProcessByLegacyRole(product: any, roleKey: string): NormalizedProductProcess | null {
  if (!isFlexibleProcessWorkflow(product)) return null;
  const flexible = Array.isArray(product?.flexibleUploadProcesses)
    ? product.flexibleUploadProcesses.map(normalizeFlexibleProcess).filter(hasProcessValue)
    : [];
  if (flexible.length === 0) return null;
  const def = LEGACY_PROCESS_DEFS.find((d) => d.key === roleKey);
  if (!def) return null;
  const named = flexible.find((p: NormalizedProductProcess) => {
    const title = p.title.toLowerCase();
    return def.names.some((name) => title.includes(name));
  });
  if (named) return named;
  return null;
}

function findLegacyProcess(product: any, roleKey: string): NormalizedProductProcess | null {
  const def = LEGACY_PROCESS_DEFS.find((d) => d.key === roleKey);
  if (!def) return null;
  const process = normalizeLegacyProcess(product, def);
  return hasProcessValue(process) ? process : null;
}

export function getProcessForLegacyRole(product: any, roleKey: string): NormalizedProductProcess | null {
  if (isFlexibleProcessWorkflow(product)) {
    return findFlexibleProcessByLegacyRole(product, roleKey);
  }
  return findLegacyProcess(product, roleKey);
}

export function mapLegacyProcessFormFields(product: any, convertMedia: (media: any[]) => any[]) {
  const designing = getProcessForLegacyRole(product, "designing");
  const weaving = getProcessForLegacyRole(product, "weaving");
  const dying = getProcessForLegacyRole(product, "dying");
  const finishing = getProcessForLegacyRole(product, "finishing");
  return {
    designer: designing?.karigar?.name || "",
    designerMedia: designing?.media?.length ? convertMedia(designing.media) : "",
    weaver: weaving?.karigar?.name || "",
    weaverMedia: weaving?.media?.length ? convertMedia(weaving.media) : "",
    dyer: dying?.karigar?.name || "",
    dyerMedia: dying?.media?.length ? convertMedia(dying.media) : "",
    finisher: finishing?.karigar?.name || "",
    finisherMedia: finishing?.media?.length ? convertMedia(finishing.media) : "",
  };
}

export function getProcessCompletionFlags(product: any) {
  const processes = getFlexibleFirstProcesses(product);
  const hasKarigar = processes.some((process) => Boolean(process.karigar?.name));
  const hasMedia = processes.some((process) =>
    process.media?.some((m: any) => m?.mediaURL || m?.mediaUrl)
  );
  return {
    hasAnyProcess: processes.length > 0,
    hasKarigar,
    hasMedia,
    isComplete: hasKarigar && hasMedia,
    processes,
  };
}

export function processNameKey(name: string): string {
  const n = String(name || "").trim().toLowerCase();
  for (const def of LEGACY_PROCESS_DEFS) {
    if (def.names.includes(n) || def.title.toLowerCase() === n) {
      return def.key;
    }
  }
  return n;
}

export function matchProcessMasterOption<T extends { label?: string }>(
  options: T[] | undefined,
  title: string
): T | null {
  if (!options || !title) {
    return null;
  }
  const key = processNameKey(title);
  return options.find((opt) => processNameKey(String(opt.label || "")) === key) || null;
}
